import React, { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Box, Button, Grid, TextField } from "@mui/material";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLMerchantListByTypesQuery } from "store/services/merchantApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useAuth } from "hooks";

const VehicleInsuranceForm = ({ vehicleInsurance, onSubmit, onCancel }) => {
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      provider_name: "",
      policy_number: "",
      merchant: "",
      start_date: "",
      expiry_date: "",
      coverage_details: "",
      premium_amount: "",
      status: true,
      account: viewEntity?.entity?.account || "",
      entity: viewEntity?.entity || "",
    },
  });

  // Watch start_date for expiry_date validation
  const startDate = watch("start_date");
  const _watchAccount = watch("account");
  const _watchEntity = watch("entity");

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchMerchants, { data: merchants, isLoading: loadingMerchants }] =
    useLazyGetDDLMerchantListByTypesQuery();

  // Fetch entities when account changes
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch merchants with merchantType: "Insurance" when account and entity change
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchMerchants({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        merchantType: "Insurance",
        active: "true",
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchMerchants]);

  // Populate form data when editing an existing vehicleInsurance record
  useEffect(() => {
    if (vehicleInsurance) {
      reset({
        provider_name: vehicleInsurance.provider_name || "",
        policy_number: vehicleInsurance.policy_number || "",
        merchant: vehicleInsurance.merchant || "",
        start_date: formatDate(vehicleInsurance.start_date) || "",
        expiry_date: formatDate(vehicleInsurance.expiry_date) || "",
        coverage_details: vehicleInsurance.coverage_details || "",
        premium_amount: vehicleInsurance.premium_amount || "",
        status: vehicleInsurance.status !== undefined ? vehicleInsurance.status : true,
        account: vehicleInsurance.account || viewEntity?.entity?.account || "",
        entity: vehicleInsurance.entity || viewEntity?.entity || "",
      });
    }
  }, [vehicleInsurance, reset, viewEntity]);

  // Helper function to format date values to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Form submission handler
  const onFormSubmit = (data) => {
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      merchant: data?.merchant?._id,
    };
    onSubmit(payload);
    reset({
      provider_name: "",
      policy_number: "",
      merchant: "",
      start_date: "",
      expiry_date: "",
      coverage_details: "",
      premium_amount: "",
      status: true,
      account: viewEntity?.entity?.account || "",
      entity: viewEntity?.entity || "",
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} padding={3}>
      <Grid container spacing={2}>
        {/* Account Field (for AccountAdmin or SystemAdmin) */}
        {(isAccountAdmin || isSystemAdmin) && (
          <Grid item xs={12} md={6}>
            <Controller
              name="account"
              control={control}
              rules={{ required: "Please select an account." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Account"
                  required
                  value={field.value ?? ""}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("entity", "");
                    setValue("merchant", "");
                  }}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={accounts || []}
                  loading={loadingAccounts}
                  loadingText="Loading Accounts..."
                  disabled={isAccountAdmin}
                  helperText={errors?.account?.message}
                  error={!!errors?.account}
                />
              )}
            />
          </Grid>
        )}
        {/* Entity Field (for AccountAdmin or SystemAdmin) */}
        {(isAccountAdmin || isSystemAdmin) && (
          <Grid item xs={12} md={6}>
            <Controller
              name="entity"
              control={control}
              rules={{ required: "Please select an entity." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Entity"
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("merchant", "");
                  }}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={entities || []}
                  loading={loadingEntities}
                  loadingText="Loading Entities..."
                  helperText={errors?.entity?.message}
                  error={!!errors?.entity}
                />
              )}
            />
          </Grid>
        )}
        {/* Merchant Field (Insurance Provider) */}
        <Grid item xs={12} md={12}>
          <Controller
            name="merchant"
            control={control}
            rules={{ required: "Please select an insurance provider." }}
            render={({ field: { value, onChange } }) => (
              <BaseAutocomplete
                fullWidth
                label="Insurance Provider"
                required
                value={merchants?.find((m) => m._id === value?._id) ?? value ?? null}
                onChange={(_, data) => onChange(data)}
                getOptionLabel={(opt) => (opt ? `${opt.merchantName} (${opt.merchantCode})` : "")}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                options={merchants || []}
                loading={loadingMerchants}
                loadingText="Loading Insurance Providers..."
                helperText={errors?.merchant?.message}
                error={!!errors?.merchant}
              />
            )}
          />
        </Grid>
        {/* Provider Name Field */}
        <Grid item xs={12} md={12}>
          <Controller
            name="provider_name"
            control={control}
            rules={{ required: "Provider Name is required" }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Provider Name"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors.provider_name}
                helperText={errors.provider_name?.message}
              />
            )}
          />
        </Grid>
        {/* Policy Number Field */}
        <Grid item xs={12} md={12}>
          <Controller
            name="policy_number"
            control={control}
            rules={{ required: "Policy Number is required" }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Policy Number"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors.policy_number}
                helperText={errors.policy_number?.message}
              />
            )}
          />
        </Grid>
        {/* Start Date Field */}
        <Grid item xs={12} md={6}>
          <Controller
            name="start_date"
            control={control}
            rules={{
              required: "Start Date is required",
              validate: {
                notFuture: (value) => {
                  if (!value) return true;
                  const today = new Date();
                  today.setHours(0, 0, 0, 0); // Normalize to start of day
                  const start = new Date(value);
                  return start <= today || "Start Date cannot be in the future";
                },
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors.start_date}
                helperText={errors.start_date?.message}
              />
            )}
          />
        </Grid>
        {/* Expiry Date Field */}
        <Grid item xs={12} md={6}>
          <Controller
            name="expiry_date"
            control={control}
            rules={{
              required: "Expiry Date is required",
              validate: {
                afterStart: (value) => {
                  if (!value || !startDate) return true;
                  const start = new Date(startDate);
                  const expiry = new Date(value);
                  return expiry > start || "Expiry Date must be after Start Date";
                },
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors.expiry_date}
                helperText={errors.expiry_date?.message}
              />
            )}
          />
        </Grid>
        {/* Coverage Details Field */}
        <Grid item xs={12} md={12}>
          <Controller
            name="coverage_details"
            control={control}
            rules={{ required: "Coverage Details are required" }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Coverage Details"
                multiline
                rows={4}
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors.coverage_details}
                helperText={errors.coverage_details?.message}
              />
            )}
          />
        </Grid>
        {/* Premium Amount Field */}
        <Grid item xs={12} md={12}>
          <Controller
            name="premium_amount"
            control={control}
            rules={{
              required: "Premium Amount is required",
              min: {
                value: 0,
                message: "Premium Amount cannot be negative",
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Premium Amount"
                type="number"
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors.premium_amount}
                helperText={errors.premium_amount?.message}
              />
            )}
          />
        </Grid>
        {/* Status Field */}
        {vehicleInsurance && (
          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => <AppSwitch {...field} label="Status?" />}
            />
          </Grid>
        )}
        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Submit
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VehicleInsuranceForm;